import { useContext, createContext, FC, PropsWithChildren, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useMappingQuery } from 'api/MappingsAPI';
import { addNegativeToastWithErrorId } from 'modules/toastContainer';
import { DataTypeType } from 'modules/domains';
import { ESTypeMap, ESFieldTypeMap } from 'modules/workflows/types';
import { getParsedFieldMappings } from './FieldMappingsContext.utils';

export type FieldMappingsState = {
  isLoading: boolean;
  didError: boolean;
  fieldMappings: ESTypeMap;
  getFieldMappingsByDataType: (dataType: DataTypeType) => void;
};

export const defaultFieldMappingsState = {
  isLoading: false,
  didError: false,
  fieldMappings: {},
  getFieldMappingsByDataType: () => {},
};

const fetchOptions = {
  refetchOnMount: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
};

export const FieldMappingsContext = createContext<FieldMappingsState>(defaultFieldMappingsState);
export const useFieldMappingsContext = () => useContext(FieldMappingsContext);

export const FieldMappingsContextProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const dispatch = useDispatch();
  const {
    data: mappings = [],
    isError: didError,
    isLoading,
  } = useMappingQuery(
    {},
    {
      ...fetchOptions,
      onError: (error) => {
        dispatch(
          addNegativeToastWithErrorId(
            'Problem fetching mappings',
            'FieldMappingsContextProvider',
            error,
          ),
        );
      },
    },
  );

  const fieldMappings = useMemo(() => getParsedFieldMappings(mappings), [mappings]);

  const getFieldMappingsByDataType = (dataType: DataTypeType): ESFieldTypeMap =>
    fieldMappings[dataType] ?? {};

  const value = {
    fieldMappings,
    didError,
    isLoading,
    getFieldMappingsByDataType,
  };

  return <FieldMappingsContext.Provider value={value}>{children}</FieldMappingsContext.Provider>;
};
